import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  RefreshControl,
  ActivityIndicator,
  Modal,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import { useNavigation } from '@react-navigation/native';
import { useAuth } from '../context/AuthContext';
import { examAPI } from '../services/api';
import { Logger } from '../utils/logger';
import { academicYearOptions, enrollmentIdForYear, examToSelect, yearLabel } from '../utils/examResults';
import FeatherIcon from 'react-native-vector-icons/Feather';
import { useReportCardPDF } from '../hooks/useReportCardPDF';
import { useStudentProfilePhoto } from '../hooks/useStudentProfilePhoto';
import { Toast } from '../components/common/Toast';
import { ReportCardsSection } from '../components/reportCards/ReportCardsSection';
import { WebPageShell } from '../components/common/WebPageShell';
import { Colors, Spacing, FontSize, BorderRadius } from '../theme/colors';
import { useTheme, ThemeColors } from '../context/ThemeContext';
import type { ExamResult, ExamSubjectResult } from '../types';

type TabType = 'results' | 'schedule';

interface ExamSchedule {
  id: string;
  examId: string;
  examName: string;
  subject: string;
  examDate: string;
  startTime: string;
  endTime: string;
  room?: string;
  maxMarks?: number;
  duration?: string;
  invigilator?: string;
}

export interface ExamTypeModel {
  id: string;
  name: string;
  frequency: string;
  createdBy: string | null;
  createdDate: string;
  modifiedBy: string | null;
  modifiedDate: string | null;
  isActive: boolean;
  isYearEnding?: boolean;
}

interface SubjectPerformance {
  subject: string;
  bestPercentage: number;
  grade: string;
}

const GRADE_COLOR: Record<string, string> = {
  'A+': Colors.success, A: Colors.success,
  'B+': Colors.info, B: Colors.info,
  'C+': Colors.warning, C: Colors.warning,
  D: Colors.error, F: Colors.error,
};

const daysUntil = (iso: string) => {
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
};

/** Minutes since midnight for a clock string such as "09:30", "09:30:00" or "9:30 AM"; null when it is not one. */
const clockMinutes = (value?: string | null): number | null => {
  const m = /(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?/i.exec(value ?? '');
  if (!m) return null;
  let hours = Number(m[1]);
  const mins = Number(m[2]);
  const meridiem = m[3]?.toUpperCase();
  if (meridiem === 'PM' && hours < 12) hours += 12;
  if (meridiem === 'AM' && hours === 12) hours = 0;
  return hours * 60 + mins;
};

/** "9:30 AM" for a clock string; the original text when it cannot be read. */
const formatClock = (value?: string | null): string => {
  const total = clockMinutes(value);
  if (total === null) return value ?? '';
  const h24 = Math.floor(total / 60);
  const mm = String(total % 60).padStart(2, '0');
  return `${h24 % 12 === 0 ? 12 : h24 % 12}:${mm} ${h24 >= 12 ? 'PM' : 'AM'}`;
};

type ExamPhase = 'upcoming' | 'live' | 'completed';

// Exam dates and start/end times are wall-clock times, compared with the device's own clock - the
// same "parent's local time" the rest of this app uses (see utils/dateUtils.ts).
const schoolNow = (now: Date): { day: string; minutes: number } => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    day: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
    minutes: now.getHours() * 60 + now.getMinutes(),
  };
};

/** The exam's calendar day as YYYY-MM-DD, read from the text so no timezone can shift it. */
const examDay = (examDate: string): string | null => {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(examDate ?? '');
  if (m) return m[1];
  const d = new Date(examDate);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** Where an exam paper stands right now: before its start time, within its window, or after it. */
const examPhase = (exam: { examDate: string; startTime: string; endTime: string }, now: Date): ExamPhase => {
  const day = examDay(exam.examDate);
  if (!day) return 'upcoming';
  const today = schoolNow(now);
  if (day < today.day) return 'completed';
  if (day > today.day) return 'upcoming';
  const start = clockMinutes(exam.startTime);
  const end = clockMinutes(exam.endTime);
  if (start !== null && today.minutes < start) return 'upcoming';
  if (end !== null && today.minutes > end) return 'completed';
  if (start !== null) return 'live';
  return 'upcoming';
};

const EXAM_PHASE_LABEL: Record<ExamPhase, string> = {
  upcoming: 'Upcoming Exam',
  live: 'LIVE NOW',
  completed: 'Completed',
};

export function ExamResultsScreen({ isNested }: { isNested?: boolean }) {
  const { selectedStudent } = useAuth();
  const navigation = useNavigation<any>();
  const { displayPhoto: studentProfilePhoto } = useStudentProfilePhoto(
    selectedStudent?.id ?? '',
    selectedStudent ?? undefined
  );
  const [scheduleListModalVisible, setScheduleListModalVisible] = useState(false);
  // Re-read every 30s so the banner moves from Upcoming to Live to Completed on its own.
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [results, setResults] = useState<ExamResult[]>([]);
  const [schedule, setSchedule] = useState<ExamSchedule[]>([]);
  const [rawSchedule, setRawSchedule] = useState<any[]>([]);
  const [selectedExam, setSelectedExam] = useState<string>('');
  const [allExamTypes, setAllExamTypes] = useState<ExamTypeModel[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<string>('');
  const [availableYears, setAvailableYears] = useState<string[]>([]);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info'>('success');
  const [selectedExamSchedule, setSelectedExamSchedule] = useState<ExamSchedule | null>(null);
  const [examDetailModalVisible, setExamDetailModalVisible] = useState(false);
  const { generatePDF, downloading } = useReportCardPDF();
  // Bumped by pull-to-refresh so the report-card list reloads with the rest.
  const [reportCardsRefresh, setReportCardsRefresh] = useState(0);
  const { colors, isDark } = useTheme();
  const styles = useMemo(() => createStyles(colors, isDark), [colors, isDark]);
  const { width: winWidth } = useWindowDimensions();
  const isMobile = winWidth < 768;

  const openFilterModal = () => {
    setFilterModalVisible(true);
  };

  const closeFilterModal = () => {
    setFilterModalVisible(false);
  };

  const showToast = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setToastMessage(message);
    setToastType(type);
    setToastVisible(true);
  };

  // The years the child was enrolled, plus the current one (utils/examResults); the current
  // year is selected first.
  useEffect(() => {
    const years = academicYearOptions(selectedStudent);
    setAvailableYears(years);
    if (years.length === 0) return;
    const current = selectedStudent?.academicYear ? yearLabel(selectedStudent.academicYear) : '';
    setSelectedYear(prev => (prev && years.includes(prev) ? prev : years.includes(current) ? current : years[0]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudent?.id, selectedStudent?.academicYear]);

  // Only exam types whose results are PUBLISHED for this child in the chosen
  // year - an unpublished exam would open onto an empty page.
  useEffect(() => {
    if (!selectedStudent || !selectedYear) return;
    const enrollmentId = enrollmentIdForYear(selectedStudent, selectedYear);
    if (!enrollmentId) {
      // Not enrolled that year: nothing published, and nothing from another year shown.
      setAllExamTypes([]);
      setSelectedExam('');
      return;
    }
    examAPI.getPublishedExamTypes(enrollmentId)
      .then(res => {
        const types: ExamTypeModel[] = res.data || [];
        setAllExamTypes(types);
        setSelectedExam(current => examToSelect(types, current));
      })
      .catch(err => {
        Logger.error('ExamResults', 'Failed to load published exam types', err);
        setAllExamTypes([]);
        setSelectedExam('');
      });
  }, [selectedStudent, selectedYear]);

  const loadData = useCallback(async (showLoader = true) => {
    if (!selectedStudent || !selectedYear || !selectedExam) {
      // No exam for this year: clear the table, or the last year's marks stay on screen.
      setResults([]);
      setLoading(false);
      setRefreshing(false);
      return;
    }

    if (showLoader) {
      setLoading(true);
    }

    Logger.info('ExamResults', `Loading results for ${selectedStudent.id}, year: ${selectedYear}, exam: ${selectedExam}`);
    try {
      // Extract current (start) year from "2025-2026" format for API calls
      const yearParts = selectedYear.split('-');
      const currentYearStr = yearParts.length > 0 ? yearParts[0] : selectedYear;
      const currentYear = parseInt(currentYearStr, 10);

      // The enrolment for the selected academic year (see enrollmentIdForYear).
      const enrollmentIdToUse = enrollmentIdForYear(selectedStudent, selectedYear) ?? '';

      const [reportCardRes, scheduleRes] = await Promise.all([
        examAPI.getReportCard(
          enrollmentIdToUse,
          currentYear,
          [selectedExam]
        ).catch((err) => {
          Logger.error('ExamResults', 'Failed to load report card', err);
          return { data: { ExamTypeWisePerformance: [] } };
        }),
        examAPI.getParentExamSchedule(
          selectedStudent.classId,
          currentYearStr
        ).catch((err) => {
          Logger.error('ExamResults', 'Failed to load exam schedule', err);
          return { data: { exams: [] } };
        }),
      ]);

      const performanceData = reportCardRes.data?.examTypeWisePerformance
        || reportCardRes.data?.ExamTypeWisePerformance
        || (Array.isArray(reportCardRes.data) ? reportCardRes.data : []);

      const examResults: ExamResult[] = performanceData.map((perf: any) => ({
        examTypeId: perf.examTypeId ?? perf.ExamTypeId,
        examTypeName: perf.examTypeName ?? perf.ExamTypeName,
        examName: perf.examName ?? perf.ExamName ?? perf.examTypeName ?? perf.ExamTypeName,
        subjects: (perf.subjects ?? perf.Subjects ?? []).map(
          (subj: any): ExamSubjectResult => ({
            subjectId: subj.subjectId ?? subj.SubjectId,
            subjectName: subj.subjectName ?? subj.SubjectName,
            obtainedMarks: subj.obtainedMarks ?? subj.ObtainedMarks,
            maxMarks: subj.maxMarks ?? subj.MaxMarks,
            percentage: subj.percentage ?? subj.Percentage,
            grade: subj.grade ?? subj.Grade,
            remarks: subj.remarks ?? subj.Remarks,
          }),
        ),
        overallTotalMarks: perf.overallTotalMarks ?? perf.OverallTotalMarks,
        overallMaxMarks: perf.overallMaxMarks ?? perf.OverallMaxMarks,
        overallPercentage: perf.overallPercentage ?? perf.OverallPercentage,
        overallGrade: perf.overallGrade ?? perf.OverallGrade,
        result: perf.result ?? perf.Result,
      }));

      // Parse exam schedules from API response - supports multiple API response formats
      const examSchedules: ExamSchedule[] = [];
      const scheduleData = scheduleRes.data?.exams || scheduleRes.data?.data || [];

      Logger.info('ExamResults', 'Schedule API Response:', {
        hasExams: !!scheduleRes.data?.exams,
        hasData: !!scheduleRes.data?.data,
        scheduleDataLength: scheduleData.length,
      });

      if (Array.isArray(scheduleData) && scheduleData.length > 0) {
        scheduleData.forEach((exam: any) => {
          const examId = exam.examId ?? exam.ExamId ?? exam.id;
          const examName = exam.name ?? exam.Name ?? exam.examName ?? exam.ExamName ?? 'Exam';

          // Handle different API response structures for class schedules
          const classSchedules = exam.classSchedules ?? exam.ClassSchedules ?? [];

          if (Array.isArray(classSchedules) && classSchedules.length > 0) {
            classSchedules.forEach((cls: any) => {
              // Handle both camelCase and PascalCase response formats
              const subjectTimetable = cls.subjectTimetable ?? cls.SubjectTimetable ?? cls.subjects ?? cls.Subjects ?? [];

              if (Array.isArray(subjectTimetable) && subjectTimetable.length > 0) {
                subjectTimetable.forEach((subject: any) => {
                  const scheduleItem: ExamSchedule = {
                    id: `${examId}-${subject.subjectId ?? subject.SubjectId}`,
                    examId: examId,
                    examName: examName,
                    subject: subject.subjectName ?? subject.SubjectName ?? subject.name ?? subject.Name ?? 'Subject',
                    examDate: subject.examDate ?? subject.ExamDate ?? subject.date ?? subject.Date ?? '',
                    startTime: subject.startTime ?? subject.StartTime ?? '',
                    endTime: subject.endTime ?? subject.EndTime ?? '',
                    room: subject.room ?? subject.Room,
                    maxMarks: subject.maxMarks ?? subject.MaxMarks,
                    duration: subject.duration ?? subject.Duration,
                    invigilator: subject.invigilator ?? subject.Invigilator,
                  };
                  examSchedules.push(scheduleItem);
                });
              }
            });
          }
        });
      }

      Logger.info('ExamResults', `Loaded ${examSchedules.length} exam schedules`, {
        firstSchedule: examSchedules[0] || 'No schedules'
      });

      setResults(examResults);
      setSchedule(examSchedules);
      setRawSchedule(scheduleRes.data?.exams || []);
    } catch (err) {
      Logger.error('ExamResults', 'Load failed', err);
      setResults([]);
      setSchedule([]);
      setRawSchedule([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedStudent, selectedYear, selectedExam]);

  useEffect(() => { loadData(true); }, [loadData]);

  const selectedExamResult = results.find(r => r.examTypeId === selectedExam);
  const filtered = selectedExamResult?.subjects ?? [];
  const totalMarks = selectedExamResult?.overallTotalMarks ?? 0;
  const maxMarks = selectedExamResult?.overallMaxMarks ?? 0;
  const overall = selectedExamResult ? Math.round(selectedExamResult.overallPercentage) : 0;
  const resultStatus = selectedExamResult?.result ?? 'N/A';
  const overallGrade = selectedExamResult?.overallGrade ?? 'N/A';

  const subjectPerformance = useMemo(() => {
    if (!filtered.length) return [];
    return filtered.map(subj => ({
      subject: subj.subjectName,
      bestPercentage: Math.round(subj.percentage),
      grade: subj.grade || 'N/A',
    }));
  }, [filtered]);

  const filteredSchedule = useMemo(() => {
    if (!schedule || schedule.length === 0) return [];
    return schedule.filter(exam => exam.examId === selectedExam);
  }, [schedule, selectedExam]);

  const nextExam = useMemo(() => {
    if (!schedule || schedule.length === 0) return null;
    const futureExams = schedule.filter(ex => daysUntil(ex.examDate) >= 0);
    if (futureExams.length > 0) {
      futureExams.sort((a, b) => new Date(a.examDate).getTime() - new Date(b.examDate).getTime());
      return futureExams[0];
    }
    const pastExams = [...schedule].sort((a, b) => new Date(b.examDate).getTime() - new Date(a.examDate).getTime());
    return pastExams[0];
  }, [schedule]);

  // The paper on right now takes the banner; otherwise the next one still to come (so a paper
  // already finished earlier today never hides one that starts later); otherwise the most recent.
  const bannerExam = useMemo(() => {
    if (!schedule || schedule.length === 0) return null;
    const startOf = (ex: ExamSchedule) => `${examDay(ex.examDate) ?? ''} ${String(clockMinutes(ex.startTime) ?? 0).padStart(4, '0')}`;
    const live = schedule.find(ex => examPhase(ex, now) === 'live');
    if (live) return live;
    const coming = schedule.filter(ex => examPhase(ex, now) === 'upcoming').sort((a, b) => startOf(a).localeCompare(startOf(b)));
    if (coming.length > 0) return coming[0];
    return [...schedule].sort((a, b) => startOf(b).localeCompare(startOf(a)))[0] ?? nextExam;
  }, [schedule, nextExam, now]);
  const bannerPhase: ExamPhase | null = bannerExam ? examPhase(bannerExam, now) : null;

  const handleDownloadPDF = async () => {
    const success = await generatePDF({
      selectedStudent,
      selectedExamResult,
      selectedYear,
      filtered,
      totalMarks,
      maxMarks,
      overall,
      overallGrade,
      resultStatus,
      studentImageUrl: studentProfilePhoto,
      examSchedule: schedule
    });
    if (success) {
      showToast('Marksheet (PDF) downloaded', 'success');
    } else {
      showToast('Failed to download marksheet', 'error');
    }
  };

  // Published report cards belong to the enrolment of the selected academic year.
  const reportCardEnrollmentId = selectedStudent && selectedYear ? enrollmentIdForYear(selectedStudent, selectedYear) : undefined;

  if (loading) {
    const loader = <LoadingSpinner message="Loading exam results..." />;
    // On its own the page keeps its header while it loads; inside Academics it is just the loader.
    return isNested ? loader : <WebPageShell title="Exam Results" contentStyle={{ maxWidth: 800 }}>{loader}</WebPageShell>;
  }

  const content = (
    <View style={[styles.container, isNested && { backgroundColor: 'transparent' }]}>
      <ScrollView
        style={styles.scroll}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); setReportCardsRefresh(k => k + 1); loadData(false); }} />}
      >
        {/* Next Exam Widget */}
        {bannerExam && bannerPhase && (
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={() => navigation.navigate('ExamSchedule', { schedule: rawSchedule, initialExamId: bannerExam.examId })}
          >
            <View style={styles.nextExamWidget}>
              <View style={styles.nextExamDetails}>
                <View style={styles.nextExamIconBox}>
                  <FeatherIcon name="file-text" size={24} color="#FFFFFF" />
                </View>
                <View style={styles.nextExamInfo}>
                  {bannerPhase === 'live' ? (
                    <View style={styles.examLiveBadge}>
                      <View style={styles.examLiveDot} />
                      <Text style={styles.examLiveBadgeText}>{EXAM_PHASE_LABEL.live}</Text>
                    </View>
                  ) : (
                    <Text style={styles.examStatusLabel}>{EXAM_PHASE_LABEL[bannerPhase]}</Text>
                  )}
                  <Text style={styles.nextExamName}>{bannerExam.examName}</Text>
                  <Text style={styles.nextExamTime}>
                    {[
                      bannerExam.subject,
                      new Date(bannerExam.examDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                      bannerExam.startTime
                        ? `${formatClock(bannerExam.startTime)}${bannerExam.endTime ? ` – ${formatClock(bannerExam.endTime)}` : ''}`
                        : '',
                    ].filter(Boolean).join(' · ')}
                  </Text>
                </View>
              </View>
            </View>
          </TouchableOpacity>
        )}

        <ReportCardsSection
          enrollmentId={reportCardEnrollmentId}
          studentName={selectedStudent?.name}
          refreshKey={reportCardsRefresh}
          onOpen={card => navigation.navigate('ReportCardDetail', {
            reportCardId: card.id,
            returnTo: isNested ? 'Academics' : 'ExamResults',
          })}
          notify={showToast}
        />

        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <TouchableOpacity style={[styles.headerDropdown, { marginBottom: 0 }]} onPress={openFilterModal}>
            <Text style={styles.resultsHeader}>
              {selectedExamResult?.examName || allExamTypes.find(et => et.id === selectedExam)?.name || 'Results'} {selectedYear ? `(${selectedYear})` : ''}
            </Text>
            <FeatherIcon name="chevron-down" size={24} color={isDark ? colors.primary : '#1E3A8A'} style={{ marginLeft: 8 }} />
          </TouchableOpacity>

        </View>

        <Modal
          visible={filterModalVisible}
          transparent={true}
          animationType="fade"
          onRequestClose={closeFilterModal}
        >
          <TouchableOpacity style={styles.filterModalOverlay} activeOpacity={1} onPress={closeFilterModal}>
            <TouchableOpacity activeOpacity={1} style={styles.filterModalContent} onPress={() => { }}>
              <View style={styles.filterModalHeader}>
                <Text style={styles.filterModalTitle}>Select Exam</Text>
                <TouchableOpacity onPress={closeFilterModal} style={styles.filterModalCloseBtn}>
                  <FeatherIcon name="x" size={24} color={colors.textSecondary} />
                </TouchableOpacity>
              </View>

              <ScrollView showsVerticalScrollIndicator={false} style={{ maxHeight: '70%' }}>
                {availableYears.length > 0 && (
                  <View style={styles.filterSectionContainer}>
                    <Text style={styles.filterSectionLabel}>Academic Year</Text>
                    <View style={styles.filterYearTabs}>
                      {availableYears.map(year => (
                        <TouchableOpacity
                          key={year}
                          style={[styles.filterYearTab, selectedYear === year && styles.filterYearTabActive]}
                          onPress={() => setSelectedYear(year)}
                        >
                          <Text style={[styles.filterYearTabText, selectedYear === year && styles.filterYearTabTextActive]}>
                            {year}
                          </Text>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </View>
                )}

                <View style={styles.filterSectionContainer}>
                  <Text style={styles.filterSectionLabel}>Exam Type</Text>
                  {allExamTypes.length === 0 && (
                    <Text style={styles.emptyText}>
                      No exam results have been published for {selectedYear || 'this year'} yet.
                    </Text>
                  )}
                  <View style={styles.filterExamTypeTabs}>
                    {allExamTypes.filter(et => et.isActive).map(et => (
                      <TouchableOpacity
                        key={et.id}
                        style={[styles.filterExamTypeTab, selectedExam === et.id && styles.filterExamTypeTabActive]}
                        onPress={() => setSelectedExam(et.id)}
                      >
                        <Text style={[styles.filterExamTypeTabText, selectedExam === et.id && styles.filterExamTypeTabTextActive]}>
                          {et.name}
                        </Text>
                      </TouchableOpacity>
                    ))}
                  </View>
                </View>
              </ScrollView>

              <TouchableOpacity style={styles.filterApplyButton} onPress={closeFilterModal}>
                <Text style={styles.filterApplyButtonText}>Apply</Text>
              </TouchableOpacity>
            </TouchableOpacity>
          </TouchableOpacity>
        </Modal>

        <>
          {filtered.length > 0 ? (
            <ScrollView horizontal={isMobile} showsHorizontalScrollIndicator={isMobile}>
              <View style={[styles.tableContainer, isMobile && styles.tableContainerMobile]}>
                <View style={styles.tableHeader}>
                  <Text style={[styles.tableHeaderText, { flex: 2 }]}>SUBJECT</Text>
                  <Text style={[styles.tableHeaderText, { flex: 1, textAlign: 'center' }]}>MARKS</Text>
                  <Text style={[styles.tableHeaderText, { flex: 1, textAlign: 'center' }]}>AVG</Text>
                  <Text style={[styles.tableHeaderText, { flex: 1, textAlign: 'center' }]}>GRADE</Text>
                </View>

                {filtered.map((result, idx) => {
                  const pct = Math.round(result.percentage);
                  const avgMock = Math.max(0, pct - 5);

                  let gradeColor = { bg: '#E0F2FE', text: '#0284C7' };
                  if (result.grade === 'A+' || result.grade === 'A') gradeColor = { bg: '#DCFCE7', text: '#16A34A' };
                  if (result.grade === 'B+' || result.grade === 'B') gradeColor = { bg: '#E0F2FE', text: '#0284C7' };
                  if (result.grade === 'C+' || result.grade === 'C') gradeColor = { bg: '#FEF3C7', text: '#D97706' };
                  if (result.grade === 'D' || result.grade === 'F') gradeColor = { bg: '#FEE2E2', text: '#DC2626' };

                  return (
                    <View key={result.subjectId} style={[styles.tableRow, idx === filtered.length - 1 && { borderBottomWidth: 0 }]}>
                      <Text style={[styles.tableRowText, { flex: 2, fontWeight: '600', color: colors.textPrimary }]}>{result.subjectName}</Text>
                      <Text style={[styles.tableRowText, { flex: 1, textAlign: 'center', fontWeight: '700', color: colors.textPrimary }]}>{result.obtainedMarks}/{result.maxMarks}</Text>
                      <Text style={[styles.tableRowText, { flex: 1, textAlign: 'center', color: colors.textSecondary }]}>{avgMock}%</Text>
                      <View style={[styles.gradeBadgeContainer, { flex: 1 }]}>
                        <View style={[styles.gradeBadge, { backgroundColor: gradeColor.bg }]}>
                          <Text style={[styles.gradeBadgeText, { color: gradeColor.text }]}>{result.grade}</Text>
                        </View>
                      </View>
                    </View>
                  );
                })}
              </View>
            </ScrollView>
          ) : (
            <View style={styles.emptyContainer}>
              <FeatherIcon name="file-text" size={48} color={colors.textMuted} style={styles.emptyIcon} />
              <Text style={styles.emptyTitle}>No Results Yet</Text>
              <Text style={styles.emptyText}>Marks and grades will appear here once the exam results are published.</Text>
            </View>
          )}

          {filtered.length > 0 && (
            <View style={[styles.footer, { backgroundColor: 'transparent', paddingHorizontal: 0, paddingBottom: 32 }]}>
              <TouchableOpacity
                style={[styles.downloadButton, downloading && { opacity: 0.7 }]}
                onPress={handleDownloadPDF}
                disabled={downloading}
              >
                {downloading ? (
                  <ActivityIndicator color="#FFFFFF" size="small" />
                ) : (
                  <View style={styles.downloadButtonContent}>
                    <FeatherIcon name="download" size={20} color="#FFFFFF" style={styles.downloadIcon} />
                    <Text style={styles.downloadButtonText}>Download marksheet</Text>
                  </View>
                )}
              </TouchableOpacity>
            </View>
          )}
        </>
      </ScrollView>

      {/* Schedule List Modal */}
      <Modal visible={scheduleListModalVisible} transparent animationType="fade" onRequestClose={() => setScheduleListModalVisible(false)}>
        <View style={styles.modalOverlay}>
          <View style={[styles.scheduleModalContent, { maxHeight: '80%', padding: 0 }]}>
            <View style={[styles.scheduleModalHeader, { paddingHorizontal: 24, paddingTop: 24 }]}>
              <Text style={styles.scheduleModalTitle}>Exam Schedule</Text>
              <TouchableOpacity onPress={() => setScheduleListModalVisible(false)} style={styles.scheduleModalCloseBtn}>
                <FeatherIcon name="x" size={24} color={colors.textSecondary} />
              </TouchableOpacity>
            </View>
            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingHorizontal: 24, paddingBottom: 24 }}>
              {filteredSchedule.length > 0 ? (
                <View style={styles.scheduleContainer}>
                  {filteredSchedule.map((exam) => (
                    <TouchableOpacity
                      key={exam.id}
                      style={styles.scheduleCard}
                      onPress={() => {
                        setScheduleListModalVisible(false);
                        setSelectedExamSchedule(exam);
                        setExamDetailModalVisible(true);
                      }}
                    >
                      <View style={styles.scheduleCardTop}>
                        <View style={styles.scheduleExamBadge}>
                          <Text style={styles.scheduleExamName}>{exam.examName}</Text>
                        </View>
                      </View>
                      <Text style={styles.scheduleSubject}>{exam.subject}</Text>
                      <View style={styles.scheduleCardFooter}>
                        <FeatherIcon name="calendar" size={14} color={colors.textSecondary} style={{ marginRight: 6 }} />
                        <Text style={styles.scheduleDate}>
                          {new Date(exam.examDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </Text>
                      </View>
                      <View style={[styles.scheduleCardFooter, { marginTop: 4 }]}>
                        <FeatherIcon name="clock" size={14} color={colors.textSecondary} style={{ marginRight: 6 }} />
                        <Text style={styles.scheduleTime}>{exam.startTime} - {exam.endTime}</Text>
                      </View>
                    </TouchableOpacity>
                  ))}
                </View>
              ) : (
                <View style={[styles.emptyContainer, { borderWidth: 0, paddingVertical: 40 }]}>
                  <FeatherIcon name="calendar" size={48} color={colors.textMuted} style={styles.emptyIcon} />
                  <Text style={styles.emptyTitle}>No Schedule</Text>
                  <Text style={styles.emptyText}>Exam schedule will appear here once it's published.</Text>
                </View>
              )}
            </ScrollView>
          </View>
        </View>
      </Modal>

      {/* Exam Schedule Detail Modal */}
      {selectedExamSchedule && (
        <Modal visible={examDetailModalVisible} transparent animationType="fade" onRequestClose={() => setExamDetailModalVisible(false)}>
          <View style={styles.modalOverlay}>
            <View style={styles.scheduleModalContent}>
              <ScrollView showsVerticalScrollIndicator={false}>
                {/* Header */}
                <View style={styles.scheduleModalHeader}>
                  <View>
                    <Text style={styles.scheduleModalTitle}>{selectedExamSchedule.subject}</Text>
                    <Text style={styles.scheduleModalExamName}>{selectedExamSchedule.examName}</Text>
                  </View>
                  <TouchableOpacity style={styles.scheduleModalCloseBtn} onPress={() => setExamDetailModalVisible(false)}>
                    <FeatherIcon name="x" size={20} color={colors.textPrimary} />
                  </TouchableOpacity>
                </View>

                {/* Exam Details */}
                <View style={styles.scheduleDetailSection}>
                  <View style={styles.detailRow}>
                    <FeatherIcon name="calendar" size={16} color={colors.textSecondary} />
                    <View style={styles.detailContent}>
                      <Text style={styles.detailLabel}>Exam Date</Text>
                      <Text style={styles.detailValue}>
                        {new Date(selectedExamSchedule.examDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                      </Text>
                    </View>
                  </View>

                  <View style={styles.detailRow}>
                    <FeatherIcon name="clock" size={16} color={colors.textSecondary} />
                    <View style={styles.detailContent}>
                      <Text style={styles.detailLabel}>Time</Text>
                      <Text style={styles.detailValue}>{selectedExamSchedule.startTime} - {selectedExamSchedule.endTime}</Text>
                    </View>
                  </View>

                  {selectedExamSchedule.duration && (
                    <View style={styles.detailRow}>
                      <FeatherIcon name="hourglass" size={16} color={colors.textSecondary} />
                      <View style={styles.detailContent}>
                        <Text style={styles.detailLabel}>Duration</Text>
                        <Text style={styles.detailValue}>{selectedExamSchedule.duration}</Text>
                      </View>
                    </View>
                  )}

                  {selectedExamSchedule.room && (
                    <View style={styles.detailRow}>
                      <FeatherIcon name="home" size={16} color={colors.textSecondary} />
                      <View style={styles.detailContent}>
                        <Text style={styles.detailLabel}>Room/Hall</Text>
                        <Text style={styles.detailValue}>{selectedExamSchedule.room}</Text>
                      </View>
                    </View>
                  )}

                  {selectedExamSchedule.maxMarks && (
                    <View style={styles.detailRow}>
                      <FeatherIcon name="award" size={16} color={colors.textSecondary} />
                      <View style={styles.detailContent}>
                        <Text style={styles.detailLabel}>Total Marks</Text>
                        <Text style={styles.detailValue}>{selectedExamSchedule.maxMarks}</Text>
                      </View>
                    </View>
                  )}

                  {selectedExamSchedule.invigilator && (
                    <View style={styles.detailRow}>
                      <FeatherIcon name="user" size={16} color={colors.textSecondary} />
                      <View style={styles.detailContent}>
                        <Text style={styles.detailLabel}>Invigilator</Text>
                        <Text style={styles.detailValue}>{selectedExamSchedule.invigilator}</Text>
                      </View>
                    </View>
                  )}
                </View>

                {/* Close Button */}
                <TouchableOpacity style={styles.scheduleCloseButton} onPress={() => setExamDetailModalVisible(false)}>
                  <Text style={styles.scheduleCloseButtonText}>Close</Text>
                </TouchableOpacity>
              </ScrollView>
            </View>
          </View>
        </Modal>
      )}

      <Toast
        visible={toastVisible}
        message={toastMessage}
        type={toastType}
        onDismiss={() => setToastVisible(false)}
      />
    </View>
  );

  if (isNested) {
    return content;
  }

  return <WebPageShell title="Exam Results" contentStyle={{ maxWidth: 800 }}>{content}</WebPageShell>;
}

const createStyles = (colors: ThemeColors, isDark: boolean) => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.background },
  centered: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.background },
  loadingText: { marginTop: 12, color: colors.textSecondary, fontSize: 14 },
  scroll: { flex: 1, paddingHorizontal: 8, paddingTop: 0 },

  nextExamWidget: {
    backgroundColor: isDark ? colors.primary : '#347FD5',
    borderRadius: 16,
    padding: 16,
    marginBottom: 24,
  },
  nextExamTitle: {
    color: 'rgba(255,255,255,0.7)',
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    marginBottom: 12,
  },
  nextExamDetails: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  nextExamIconBox: {
    // backgroundColor: 'rgba(255,255,255,0.15)',
    // borderRadius: 12,
    // padding: 12,
    marginRight: 12,
  },
  nextExamIcon: {
    fontSize: 20,
  },
  nextExamInfo: {
    flex: 1,
  },
  nextExamSubject: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 4,
  },
  nextExamTime: {
    color: 'rgba(255,255,255,0.8)',
    fontSize: 12,
  },
  examStatusLabel: {
    color: 'rgba(255,255,255,0.85)',
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginBottom: 4,
  },
  examLiveBadge: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#DC2626',
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 3,
    marginBottom: 6,
  },
  examLiveDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: '#FFFFFF',
    marginRight: 6,
  },
  examLiveBadgeText: {
    color: '#FFFFFF',
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.8,
  },
  nextExamName: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '800',
    marginBottom: 4,
  },

  resultsHeader: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.textPrimary,
  },

  tableContainer: {
    backgroundColor: colors.card,
    borderRadius: 12,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.border,
    marginBottom: 24,
  },
  tableContainerMobile: {
    minWidth: 480,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.background,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableHeaderText: {
    fontSize: 11,
    fontWeight: '700',
    color: colors.textSecondary,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableRowText: {
    fontSize: 13,
  },
  gradeBadgeContainer: {
    alignItems: 'center',
  },
  gradeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  gradeBadgeText: {
    fontSize: 12,
    fontWeight: '700',
  },

  footer: {
    padding: 16,
    backgroundColor: colors.card,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  downloadButton: {
    backgroundColor: isDark ? colors.primary : '#17253C',
    borderRadius: 8,
    paddingVertical: 14,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
  },
  downloadButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  downloadIcon: {
    marginRight: 10,
  },
  downloadButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '600',
  },
  emptyContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
    backgroundColor: colors.card,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: colors.border,
  },
  emptyIcon: {
    marginBottom: 16,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: colors.textSecondary,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: colors.textSecondary,
    textAlign: 'center',
    paddingHorizontal: 32,
    lineHeight: 20,
  },
  headerDropdown: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  filterModalOverlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterModalContent: {
    backgroundColor: colors.surface,
    borderRadius: 20,
    width: '90%',
    maxWidth: 500,
    maxHeight: '85%',
    paddingHorizontal: 24,
    paddingVertical: 24,
  },
  filterModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  filterModalTitle: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.textPrimary,
  },
  filterModalCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: colors.divider,
    justifyContent: 'center',
    alignItems: 'center',
  },
  filterSectionContainer: {
    marginBottom: 20,
  },
  filterSectionLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: colors.textSecondary,
    textTransform: 'uppercase',
    marginBottom: 10,
    letterSpacing: 0.5,
  },
  filterYearTabs: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  } as any,
  filterYearTab: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.card,
  },
  filterYearTabActive: {
    borderColor: isDark ? colors.primary : '#1C325B',
    backgroundColor: isDark ? colors.primary : '#1C325B',
  },
  filterYearTabText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  filterYearTabTextActive: {
    color: '#FFFFFF',
  },
  filterExamTypeTabs: {
    flexDirection: 'row',
    gap: 8,
    flexWrap: 'wrap',
  } as any,
  filterExamTypeTab: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: colors.border,
  },
  filterExamTypeTabActive: {
    backgroundColor: isDark ? colors.primary : '#1C325B',
  },
  filterExamTypeTabText: {
    fontSize: 13,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  filterExamTypeTabTextActive: {
    color: '#FFFFFF',
  },
  filterApplyButton: {
    backgroundColor: isDark ? colors.primary : '#1C325B',
    borderRadius: 12,
    paddingVertical: 12,
    alignItems: 'center',
    marginTop: 20,
  },
  filterApplyButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
  // Tabs
  tabsContainer: {
    flexDirection: 'row',
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    gap: 16,
  } as any,
  tab: {
    paddingVertical: 12,
    paddingHorizontal: 4,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  tabActive: {
    borderBottomColor: isDark ? colors.primary : '#1C325B',
  },
  tabText: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.textSecondary,
  },
  tabTextActive: {
    color: isDark ? colors.primary : '#1C325B',
  },
  // Schedule Tiles
  scheduleContainer: {
    gap: 12,
    marginBottom: 24,
  } as any,
  scheduleCard: {
    backgroundColor: colors.card,
    borderRadius: 16,
    padding: 16,
    borderWidth: 1,
    borderColor: colors.border,
    elevation: 2,
    shadowColor: colors.shadow,
    shadowOpacity: 0.05,
    shadowRadius: 8,
    shadowOffset: { width: 0, height: 2 },
  },
  scheduleCardTop: {
    marginBottom: 8,
  },
  scheduleExamBadge: {
    backgroundColor: colors.primaryLight,
    paddingHorizontal: 12,
    paddingVertical: 4,
    borderRadius: 16,
    alignSelf: 'flex-start',
  },
  scheduleExamName: {
    fontSize: 12,
    fontWeight: '700',
    color: isDark ? colors.primary : '#217BD6',
  },
  scheduleSubject: {
    fontSize: 16,
    fontWeight: '700',
    color: colors.textPrimary,
    marginBottom: 8,
  },
  scheduleCardFooter: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  scheduleDate: {
    fontSize: 13,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  scheduleTime: {
    fontSize: 13,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  // Schedule Modal
  modalOverlay: {
    flex: 1,
    backgroundColor: colors.overlay,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scheduleModalContent: {
    backgroundColor: colors.surface,
    borderRadius: 20,
    width: '90%',
    maxWidth: 600,
    maxHeight: '90%',
    paddingHorizontal: 24,
    paddingVertical: 24,
  } as any,
  scheduleModalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  scheduleModalTitle: {
    fontSize: 20,
    fontWeight: '800',
    color: colors.textPrimary,
    marginBottom: 4,
  },
  scheduleModalExamName: {
    fontSize: 14,
    color: colors.textSecondary,
    fontWeight: '500',
  },
  scheduleModalCloseBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: colors.divider,
    justifyContent: 'center',
    alignItems: 'center',
  },
  scheduleDetailSection: {
    marginBottom: 24,
    gap: 16,
  } as any,
  detailRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
  } as any,
  detailContent: {
    flex: 1,
  },
  detailLabel: {
    fontSize: 12,
    color: colors.textMuted,
    fontWeight: '600',
    marginBottom: 4,
  },
  detailValue: {
    fontSize: 14,
    color: colors.textPrimary,
    fontWeight: '700',
    lineHeight: 20,
  },
  scheduleCloseButton: {
    backgroundColor: isDark ? colors.primary : '#17253C',
    borderRadius: 12,
    paddingVertical: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: 16,
  },
  scheduleCloseButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '700',
  },
});
